import { useEffect, useMemo, type ReactNode } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type { MetricComparisonResponse, ServerApiClient } from '../../../data/ServerApiClient'
import { useAnalytics } from '../../../analytics'
import { CfoPageShell, type CfoShellCacheState, type CfoShellSelectOption } from './CfoPageShell'
import type { CfoNavProfile } from './cfoNav'
import { CfoPageLoading } from './CfoPageLoading'
import { CfoPlanBanner } from './CfoPlanBanner'
import { CFO_EVENTS, type CfoSurface } from './cfoTelemetry'
import type { UseCfoCopilotResult } from './useCfoCopilot'
import { useCfoAnalysis, useRegisterCfoAnalysisPage } from './analysis'
import { resolveCfoInstantCard } from './analysis/instantCardRegistry'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import type { CfoAnalysisGlDetailTarget } from './analysis'
import type { CfoAnalysisContext, CfoAnalysisVisualSpec, CfoInstantCard } from './analysis/types'

const trimStr = (value: unknown): string => String(value ?? '').trim()

// CfoInteriorPage — the shared calm-page scaffold every interior CFO page uses:
// the unified shell (nav · topbar · full-width main) + a guided pagehead. The
// per-page copilot rail is gone: AI now lives in the shared analysis modal
// (topbar Ask, or click-to-analyze on any chart/value). The page body is
// supplied via a render-prop that still receives a `copilot`-shaped object —
// now a thin shim whose `sendMessage` opens the modal seeded with that prompt —
// so insight-card "Explain" buttons keep working without per-page rewires.

export type CfoInteriorPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  /** Active nav rail id (e.g. `financials`). */
  navId: string
  /** Telemetry + copilot section tag (e.g. `cfo_financials`). */
  activeSection: CfoSurface
  /** When true, shows a visible loading bar under the pagehead (no blank screen). */
  pageLoading?: boolean
  pageLoadingLabel?: string
  /** Page header. */
  eyebrow: string
  title: string
  lede?: ReactNode
  hidePageHead?: boolean
  /** Topbar. */
  crumb: ReactNode
  asOfLabel: string
  periodOptions?: CfoShellSelectOption[]
  periodValue?: string
  onPeriodChange?: (value: string) => void
  periodAriaLabel?: string
  comparisonLabel?: string
  comparisonOptions?: CfoShellSelectOption[]
  comparisonValue?: string
  onComparisonChange?: (value: string) => void
  comparisonAriaLabel?: string
  cacheState?: CfoShellCacheState | null
  /** Copilot rail. */
  copilotSubtitle: string
  copilotPlaceholder: string
  copilotIntro?: string
  suggestedPrompts?: string[]
  askPlaceholder?: string
  /** Live comparison snapshot for copilot grounding. */
  getComparisons?: () => MetricComparisonResponse | null
  sourceTables?: string[] | null
  analysisSelection?: Record<string, any> | null
  instantCards?: CfoInstantCard[] | null
  pageFacts?: Record<string, any> | null
  pageFactsFingerprint?: string | null
  resolveInstantCard?: (input: { context: CfoAnalysisContext; visual?: CfoAnalysisVisualSpec | null; instantCards?: CfoInstantCard[] | null }) => CfoInstantCard | null
  /** Inline GL-detail opener forwarded to the analysis modal (see CfoAnalysisPageContext). */
  openGlDetail?: (target: CfoAnalysisGlDetailTarget) => boolean
  duckDbOpts?: Record<string, any> | null
  /** Rail click handler from the in-shell router. */
  onNavSelect: (navId: string) => void
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
  /** Page body, receiving the page-aware copilot for card/hero wiring. */
  children: (ctx: { copilot: UseCfoCopilotResult }) => ReactNode
}

export function CfoInteriorPage({
  appApi,
  workerClient: _workerClient = null,
  workbookId,
  navId,
  activeSection,
  eyebrow,
  title,
  lede,
  hidePageHead = false,
  crumb,
  asOfLabel,
  periodOptions,
  periodValue,
  onPeriodChange,
  periodAriaLabel,
  comparisonLabel = 'vs. prior month',
  comparisonOptions,
  comparisonValue,
  onComparisonChange,
  comparisonAriaLabel,
  cacheState = null,
  copilotSubtitle: _copilotSubtitle,
  copilotPlaceholder: _copilotPlaceholder,
  copilotIntro: _copilotIntro,
  suggestedPrompts = [],
  askPlaceholder,
  getComparisons,
  sourceTables = null,
  analysisSelection = null,
  instantCards = null,
  pageFacts = null,
  pageFactsFingerprint = null,
  resolveInstantCard: resolveInstantCardOverride,
  openGlDetail,
  duckDbOpts = null,
  onNavSelect,
  navProfile = 'full',
  runtimeProfile,
  pageLoading = false,
  pageLoadingLabel,
  children,
}: CfoInteriorPageProps) {
  const analytics = useAnalytics()
  const analysis = useCfoAnalysis()
  const pageInstantCardResolver = useMemo(
    () => resolveInstantCardOverride || ((input: { context: CfoAnalysisContext; visual?: CfoAnalysisVisualSpec | null; instantCards?: CfoInstantCard[] | null }) => (
      resolveCfoInstantCard({
        surface: activeSection,
        pageId: navId,
        sourceComponent: input.context.sourceComponent,
        metricId: input.context.metricId,
        metricLabel: input.context.metricLabel,
        period: input.context.period,
        comparison: input.context.comparison,
        asOfLabel: input.context.asOfLabel,
        sourceTables: input.context.sourceTables,
        selection: input.context.selection,
        visual: input.visual,
        grounding: input.context.grounding,
        pageFacts,
        instantCards: input.instantCards ?? instantCards,
      })
    )),
    [activeSection, instantCards, navId, pageFacts, resolveInstantCardOverride],
  )

  // Publish this page's live context so the shared modal can ground the agent
  // and seed evidence from clicked charts/values.
  useRegisterCfoAnalysisPage({
    pageId: navId,
    activeSection,
    duckDbOpts,
    getComparisons,
    sourceTables,
    selection: analysisSelection,
    period: periodValue,
    comparison: comparisonValue,
    asOfLabel,
    suggestedPrompts,
    instantCards,
    pageFactsFingerprint,
    resolveInstantCard: pageInstantCardResolver,
    openGlDetail,
  })

  // Modal-backed `copilot` shim: page bodies (and CfoInsightCards) call
  // `copilot.sendMessage(prompt, overrides)`; we route that into the shared
  // analysis modal seeded with the prompt. The other fields are inert because
  // the conversation now lives in the modal, not an inline rail.
  const copilot = useMemo<UseCfoCopilotResult>(
    () => ({
      messages: [],
      response: null,
      busy: false,
      error: '',
      input: '',
      setInput: () => {},
      sendMessage: async (text, overrides) => {
        const workspaceContext = (overrides as any)?.workspace_context && typeof (overrides as any).workspace_context === 'object'
          ? (overrides as any).workspace_context
          : null
        const contextForResolver: CfoAnalysisContext = {
          workbookId,
          pageId: navId,
          activeSection,
          period: periodValue ?? null,
          comparison: comparisonValue ?? null,
          asOfLabel,
          metricId: (overrides as any)?.selected_metric_id ?? workspaceContext?.metric_id ?? null,
          metricLabel: workspaceContext?.metric_label ?? null,
          sourceComponent: trimStr(workspaceContext?.source_component) || 'insight_card',
          sourceTables: Array.isArray(workspaceContext?.source_tables) ? workspaceContext.source_tables : sourceTables,
          selection: workspaceContext,
          grounding: null,
          instantCard: null,
        }
        analysis.openAnalysis({
          seedPrompt: text,
          metricId: contextForResolver.metricId,
          metricLabel: contextForResolver.metricLabel,
          sourceComponent: contextForResolver.sourceComponent,
          sourceTables: Array.isArray(workspaceContext?.source_tables) ? workspaceContext.source_tables : null,
          selection: workspaceContext,
          instantCard: pageInstantCardResolver({ context: contextForResolver, visual: null, instantCards }),
        })
      },
      promptChips: [],
      reset: () => {},
    }),
    [activeSection, analysis, asOfLabel, comparisonValue, instantCards, navId, pageInstantCardResolver, periodValue, sourceTables, workbookId],
  )

  useEffect(() => {
    analytics.track({
      event: CFO_EVENTS.pageView,
      surface: activeSection,
      workbook_id: workbookId || null,
      props: {
        nav_id: navId,
        runtime_mode: runtimeProfile?.mode || 'web-rich',
        viewport_width: typeof window !== 'undefined' ? window.innerWidth : null,
        duckdb_used: runtimeProfile?.mode === 'mobile-constrained' ? false : null,
        server_json_used: true,
        mobile_deferred: runtimeProfile?.mode === 'mobile-constrained',
      },
    })
  }, [activeSection, analytics, navId, runtimeProfile?.mode, workbookId])

  return (
    <CfoPageShell
      activeNavId={navId}
      onNavSelect={onNavSelect}
      brandLabel="CFO Core"
      brandSub={asOfLabel}
      crumb={crumb}
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={periodValue}
      onPeriodChange={onPeriodChange}
      periodAriaLabel={periodAriaLabel}
      comparisonLabel={comparisonLabel}
      comparisonOptions={comparisonOptions}
      comparisonValue={comparisonValue}
      onComparisonChange={onComparisonChange}
      comparisonAriaLabel={comparisonAriaLabel}
      cacheState={cacheState}
      askPlaceholder={askPlaceholder}
      onAsk={() => analysis.openAsk()}
      dataSurface={activeSection}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    >
      <CfoPlanBanner appApi={appApi} workbookId={workbookId} />
      {hidePageHead ? null : (
        <header className="pagehead rise">
          <div className="eyebrow">{eyebrow}</div>
          <h1>{title}</h1>
          {lede ? <p className="lede">{lede}</p> : null}
        </header>
      )}
      {pageLoading ? <CfoPageLoading label={pageLoadingLabel} /> : null}
      <div aria-busy={pageLoading}>{children({ copilot })}</div>
    </CfoPageShell>
  )
}

export default CfoInteriorPage
